import React from "react";
import { withUser as ssoWithUser } from "@unifiedpatents/sso";

export const ACCESS_LEVELS = ["public", "guest", "trial", "full", "admin"];

export const meetsLevel = (current, required) => {
  const currentIndex = ACCESS_LEVELS.indexOf(current);
  const requiredIndex = ACCESS_LEVELS.indexOf(required);
  if (currentIndex === -1 || requiredIndex === -1) {
    return false;
  }
  return currentIndex >= requiredIndex;
};

export const ACCESS_DOMAINS = [
  "app", "licenses", "patents",
  "ptab", "litigation", "exparte", "upc", "international", "portfolio",
  "landscapeHevc", "landscapeAvc", "landscapeVvc",
  "landscape5g", "landscapeLte", "landscape5gRan", "landscapeLteRan",
  "landscapeWifi6", "landscapeGenai",
  "repoMpeg", "repo3gpp", "repoIeee", "repoIetf",
  "manual", "invalidity", "infringement",
  "caseExport", "patentExport", "landscapeExport", "repoExport"
];

// domain suffix → role family. The nine landscapes are gated by four merged roles.
const LANDSCAPE_ROLE_FAMILIES = {
  Hevc: "mpeg",
  Avc: "mpeg",
  Vvc: "mpeg",
  "5g": "3gpp",
  Lte: "3gpp",
  "5gRan": "3gpp",
  LteRan: "3gpp",
  Wifi6: "ieee",
  Genai: "genai"
};

// domain suffix → role family, 1:1 now that IETF has its own role.
const REPO_ROLE_FAMILIES = {
  Mpeg: "mpeg",
  "3gpp": "3gpp",
  Ieee: "ieee",
  Ietf: "ietf"
};

const computeAccessLevel = (metadata, isLoggedIn) => {
  const roles = metadata?.authorization?.roles || [];
  const has = role => roles.indexOf(role) >= 0;
  // portal-admin gets the highest access level on every domain, exports included
  if (has("portal-admin")) {
    return Object.fromEntries(ACCESS_DOMAINS.map(domain => [domain, "admin"]));
  }
  const guestOrPublic = isLoggedIn ? "guest" : "public";
  const grantedOr = role => has(role) ? "full" : guestOrPublic;

  const landscapeLevel = family =>
    has(`portal-sep-landscape-${family}`) ? "full"
      : has(`portal-sep-landscape-trial-${family}`) ? "trial"
        : guestOrPublic;
  // one shared trial role backs both automated analysis types
  const autoClaimChartLevel = role =>
    has(role) ? "full"
      : has("portal-auto-claim-chart-trial") ? "trial"
        : guestOrPublic;

  const caseLevel = grantedOr("portal-case");
  const patentLevel = grantedOr("portal-patent");
  const accessLevel = {
    app: guestOrPublic,
    licenses: grantedOr("portal-license-notice"),
    patents: patentLevel,
    ptab: caseLevel,
    litigation: caseLevel,
    exparte: caseLevel,
    upc: caseLevel,
    international: caseLevel,
    portfolio: patentLevel,
    // hand-built claim charts are internal-only, hence no trial fallback
    manual: grantedOr("portal-auto-claim-chart-manual"),
    invalidity: autoClaimChartLevel("portal-auto-claim-chart-invalidity"),
    infringement: autoClaimChartLevel("portal-auto-claim-chart-infringement"),
    // exports are granted independently of data access
    caseExport: grantedOr("portal-case-export"),
    patentExport: grantedOr("portal-patent-export"),
    landscapeExport: grantedOr("portal-sep-landscape-export"),
    repoExport: grantedOr("portal-sep-repo-export")
  };
  for (const [name, family] of Object.entries(LANDSCAPE_ROLE_FAMILIES)) {
    accessLevel[`landscape${name}`] = landscapeLevel(family);
  }
  for (const [name, family] of Object.entries(REPO_ROLE_FAMILIES)) {
    accessLevel[`repo${name}`] = grantedOr(`portal-sep-repo-${family}`);
  }
  return accessLevel;
};

export const withUser = WrappedComponent => {
  const Inner = ssoWithUser(props => {
    const accessLevel = React.useMemo(
      () => computeAccessLevel(props.user && props.user.metadata, !!(props.user && props.user.status && props.user.status.isLoggedIn)),
      [props.user]
    );
    const user = props.user ? { ...props.user, accessLevel } : props.user;
    return <WrappedComponent {...props} user={user} />;
  });
  Inner.displayName = `withUser(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;
  return Inner;
};
